import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import { useUpdateUserAccountMutation } from "../../api/marathonAdmin";
import { useNotification } from "../../hooks/useNotification";

interface MarathonAccountEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    marathonId: number;
    account: any;
}

/**
 * Преобразует Unix timestamp в формат для input type="datetime-local" (YYYY-MM-DDTHH:mm)
 */
const timestampToDatetimeLocal = (timestamp: number | null | undefined): string => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Преобразует формат datetime-local обратно в Unix timestamp
 */
const datetimeLocalToTimestamp = (datetime: string): number => {
    if (!datetime) return 0;
    return Math.floor(new Date(datetime).getTime() / 1000);
};

export const MarathonAccountEditModal: React.FC<MarathonAccountEditModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    marathonId,
    account,
}) => {
    const { showSuccess, showError } = useNotification();
    const [updateUserAccount, { isLoading }] = useUpdateUserAccountMutation();
    const [formData, setFormData] = useState<any>({});

    useEffect(() => {
        if (account) {
            setFormData({
                ...account,
                // Конвертируем метки времени для отображения в инпутах
                alny_marafon_people_fee_dt: timestampToDatetimeLocal(account.alny_marafon_people_fee),
                alny_marafon_people_time_dt: timestampToDatetimeLocal(account.alny_marafon_people_time),
            });
        }
    }, [account]);

    if (!isOpen || !account) return null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        setFormData((prev: any) => ({ 
            ...prev, 
            [name]: type === 'number' ? Number(value) : value 
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            // Подготавливаем данные для отправки (конвертируем даты обратно в timestamp)
            const submissionData = { ...formData };
            submissionData.alny_marafon_people_fee = datetimeLocalToTimestamp(formData.alny_marafon_people_fee_dt);
            submissionData.alny_marafon_people_time = datetimeLocalToTimestamp(formData.alny_marafon_people_time_dt);
            
            // Удаляем временные поля для инпутов
            delete submissionData.alny_marafon_people_fee_dt;
            delete submissionData.alny_marafon_people_time_dt;
            // Статусные метки и прочее лишнее тоже можно удалить, если Laravel ругается на лишние поля,
            // но обычно FILLABLE в модели их просто проигнорирует.

            await updateUserAccount({
                accountId: account.alny_marafon_people_id,
                data: submissionData,
                marathonId,
            }).unwrap();
            
            showSuccess("Аккаунт успешно обновлен");
            if (onSuccess) onSuccess();
            onClose();
        } catch (error: any) {
            showError(error?.data?.message || error?.message || "Ошибка при обновлении аккаунта");
        }
    };

    return (
        <div className="fixed inset-0 z-[60] overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                    <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center mb-5">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white" id="modal-title">
                                Редактирование аккаунта (ID: {account.alny_marafon_people_id})
                            </h3>
                            <button onClick={onClose} className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                <span className="sr-only">Закрыть</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID пользователя</label>
                                    <input type="number" name="alny_marafon_people_user_id" value={formData.alny_marafon_people_user_id ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Статус</label>
                                    <select 
                                        name="alny_marafon_people_status" 
                                        value={formData.alny_marafon_people_status ?? 0} 
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        <option value={0}>Участвует</option>
                                        <option value={1}>Отменен</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Дата вступления</label>
                                    <input 
                                        type="datetime-local" 
                                        name="alny_marafon_people_time_dt" 
                                        value={formData.alny_marafon_people_time_dt ?? ''} 
                                        onChange={handleChange} 
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Дата оплаты</label>
                                    <input 
                                        type="datetime-local" 
                                        name="alny_marafon_people_fee_dt" 
                                        value={formData.alny_marafon_people_fee_dt ?? ''} 
                                        onChange={handleChange} 
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" 
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID марафона</label>
                                    <input type="number" name="alny_marafon_people_number" value={formData.alny_marafon_people_number ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Главный приз получен</label>
                                    <select 
                                        name="alny_marafon_people_reward" 
                                        value={formData.alny_marafon_people_reward ?? 0} 
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    >
                                        <option value={0}>Нет</option>
                                        <option value={1}>Да</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">За какой ID платит</label>
                                    <input type="number" name="alny_marafon_people_pays" value={formData.alny_marafon_people_pays ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Кто за него платит (ID)</label>
                                    <input type="number" name="alny_marafon_people_pay" value={formData.alny_marafon_people_pay ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">ID чье место занял (place)</label>
                                    <input type="number" name="alny_marafon_people_place" value={formData.alny_marafon_people_place ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Сумма возврата (amount)</label>
                                    <input type="number" name="alny_marafon_people_amount" value={formData.alny_marafon_people_amount ?? ''} onChange={handleChange} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
                                </div>
                            </div>
                            <div className="pt-4 flex justify-end gap-3">
                                <button type="button" onClick={onClose} className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none">
                                    Отмена
                                </button>
                                <button type="submit" disabled={isLoading} className="inline-flex justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none disabled:opacity-50">
                                    {isLoading ? "Сохранение..." : "Сохранить"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};
